import {
    Typography,
    Controls,
    ControlsItem,
    StopIcon,
    BookmarkBorderedIcon,
    ShareIcon,
    PlusUserIcon,
    InProgressIcon,
    FailedIcon
} from '@genestack/ui';
import React from 'react';

import {QueryLink} from './query-link';
import {wrapWithTooltip} from '../../../components/tooltip/wrap-with-tooltip';

interface Props {
    queryInfo: QueryInfo;
    isSelected: boolean;
    showOwnerName?: boolean;
    isSharedIcon?: boolean;
    isNotMyQueryIcon?: boolean;
    isBookmarkedIcon?: boolean;
}

interface StatusIconProps {
    iconComponent: React.ElementType;
    iconTooltip: string;
}

const STATUS_ICON: Record<QueryInfo['status'], StatusIconProps | null> = {
    RUNNING: {iconComponent: InProgressIcon, iconTooltip: 'Running query'},
    PENDING: {iconComponent: InProgressIcon, iconTooltip: 'Pending query'},
    COMPLETED: null,
    STOPPED: {iconComponent: StopIcon, iconTooltip: 'Stopped'},
    FAILED: {iconComponent: FailedIcon, iconTooltip: 'Failed'},
    DRAFT: null
};

// eslint-disable-next-line react/display-name
export const QueryInfoItem = React.memo(
    ({
        queryInfo,
        isSelected,
        isSharedIcon,
        isNotMyQueryIcon,
        isBookmarkedIcon,
        showOwnerName
    }: Props) => {
        const {iconComponent: StatusIcon, iconTooltip} = STATUS_ICON[queryInfo.status] || {
            iconComponent: null,
            iconTooltip: null
        };

        return (
            <QueryLink
                queryId={queryInfo.id}
                active={isSelected}
                append={
                    <Controls gap={1} align="center">
                        {isBookmarkedIcon && (
                            <ControlsItem>
                                {wrapWithTooltip(<BookmarkBorderedIcon />, 'In my bookmarks')}
                            </ControlsItem>
                        )}

                        {isSharedIcon && (
                            <ControlsItem>
                                {wrapWithTooltip(<PlusUserIcon />, 'Shared with others')}
                            </ControlsItem>
                        )}

                        {isNotMyQueryIcon && (
                            <ControlsItem title="Created by other user">
                                {wrapWithTooltip(<ShareIcon />, 'Shared with me')}
                            </ControlsItem>
                        )}

                        {StatusIcon && (
                            <ControlsItem>
                                {wrapWithTooltip(<StatusIcon />, iconTooltip)}
                            </ControlsItem>
                        )}

                        <ControlsItem>
                            <Typography
                                intent="quiet"
                                variant="body"
                                title="Number of results found"
                            >
                                {queryInfo.resultsCount}
                            </Typography>
                        </ControlsItem>
                    </Controls>
                }
            >
                {queryInfo.name}

                {showOwnerName && (
                    <Typography intent="quiet" variant="caption">
                        {queryInfo.owner.name}
                    </Typography>
                )}
            </QueryLink>
        );
    }
);
